/**
 * Pure timeline math for the narrator, kept apart from the Web Audio code so
 * it can be tested without an AudioContext.
 *
 * A timeline is an array of entries, one per sentence, in reading order:
 * `{ duration, start, end, words: [{ start }] }`. `start`/`end` are seconds
 * on the audio clock; word starts are seconds from the sentence start.
 * Entries before the point playback was last scheduled from are given
 * `-Infinity`, so they always count as already spoken.
 */

/** Back restarts the current sentence once it has been playing this long. */
export const RESTART_THRESHOLD = 1.5;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Lays entries out back to back so that `offset` seconds into entry `index`
 * falls exactly on `at`. Returns the new `{ start, end }` of every entry and
 * the time the last one ends (where the next sentence would go).
 */
export const layout = (entries, index, offset, at) => {
  const times = entries.map(() => ({ start: -Infinity, end: -Infinity }));
  if (index >= entries.length) return { times, end: at };

  let time = at - clamp(offset, 0, entries[index].duration);
  for (let i = index; i < entries.length; i++) {
    const start = time;
    time += entries[i].duration;
    times[i] = { start, end: time };
  }
  return { times, end: time };
};

/**
 * `spoken` is how many sentences the voice has reached at `now` (the last of
 * them is the one being said); `words` is the fraction of that sentence said.
 */
export const cursorAt = (entries, now) => {
  let spoken = 0;
  while (spoken < entries.length && entries[spoken].start <= now) spoken++;

  let words = 1;
  const current = entries[spoken - 1];
  if (current && now < current.end && current.words.length) {
    const elapsed = now - current.start;
    const said = current.words.filter(word => word.start <= elapsed).length;
    words = said / current.words.length;
  }
  return { spoken, words };
};

/** Whether cursor `a` is further into the reading than cursor `b`. */
export const isAhead = (a, b) =>
  a.spoken > b.spoken || (a.spoken === b.spoken && a.words > b.words);

/**
 * Where to pick up from after a pause at `time`: the first sentence not yet
 * finished, and how far into it. Past the end, `index` is `entries.length`.
 */
export const resumePoint = (entries, time) => {
  const index = entries.findIndex(entry => entry.end > time);
  if (index === -1) return { index: entries.length, offset: 0 };
  const entry = entries[index];
  return { index, offset: clamp(time - entry.start, 0, entry.duration) };
};

/**
 * Standard player "back": restart the current sentence if it is well under
 * way, otherwise go to the previous one that has a voice (a silent sentence
 * takes no time, so landing on one would look like nothing happened).
 */
export const backTarget = (entries, current, elapsed) => {
  if (current < 0) return 0;
  if (elapsed > RESTART_THRESHOLD && entries[current]?.duration > 0) return current;

  let index = current - 1;
  while (index > 0 && !(entries[index].duration > 0)) index--;
  return Math.max(index, 0);
};

/** The next sentence, or -1 when it has not arrived yet. */
export const forwardTarget = (entries, current) =>
  current >= 0 && current + 1 < entries.length ? current + 1 : -1;
